import { RMSvgIconImage } from 'components/customComponents/CommonComponents/RMSvgIcon';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import React from 'react';
import { Group, Image, Transformer } from 'react-konva';
import {
  DrawingType,
  DrawingTypeToOverlayToolMapping,
  DrawingUpdateDetails,
  minDrawingHeight,
  minDrawingWidth,
  OverlayTools,
  resizableShapes,
} from '../constants';
import { useIsCreatingNewDrawing, useSelectedDrawingTool } from 'redux/hooks/well';

const DrawingToolsPointerClassNameMapping = {
  [OverlayTools.POINTER]: 'cursor-default',
  [OverlayTools.ANNOTATION]: 'crosshair-pointer',
  [OverlayTools.CRYSTAL]: 'crosshair-pointer',
  [OverlayTools.DROPLOCATION]: 'crosshair-pointer',
  [OverlayTools.POI]: 'crosshair-pointer',
  [OverlayTools.ROI]: 'crosshair-pointer',
  [OverlayTools.RULER]: 'crosshair-pointer',
  [OverlayTools.SCRIBBLE]: 'crosshair-pointer',
};

export interface withDrawingContainerProps {
  rectRef?: React.Ref<any>;
  id: number;
  index: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  tpObject: TransformPoints;
  isSelected: boolean;
  isLocked: boolean;
  expanded?: boolean;
  updateDrawing: (
    drawingId: number,
    drawingType: DrawingType,
    drawingUpdateDetails: DrawingUpdateDetails,
  ) => void;
  saveAndUpdateDrawing: (
    drawingId: number,
    drawingType: DrawingType,
    drawingUpdateDetails: DrawingUpdateDetails,
  ) => void;
  selectDrawing: (drawingId: number, drawingType: DrawingType) => void;
  selectDrawingTool: (drawingTool: OverlayTools) => void;
}

const lockedIcon = RMSvgIconImage({ name: 'LockDrawing' });
let updatedDrawingSizeDetails = null;
const withDrawingContainer = <
  P extends withDrawingContainerProps = withDrawingContainerProps,
>(
  WrappedComponent: React.FC<P>,
  drawingType: DrawingType,
  moveActionPreProcessor?: (Props: P, x: number, y: number) => DrawingUpdateDetails,
  transformActionPreProcessor?: (
    Props: P,
    width: number,
    height: number,
  ) => DrawingUpdateDetails,
) => {
  const ContainerComponent = (props: P) => {
    const drawingRef = React.useRef<any>();
    const trRef = React.useRef(null);
    const {
      id,
      x,
      y,
      width,
      height,
      isLocked,
      isSelected,
      updateDrawing,
      saveAndUpdateDrawing,
      selectDrawing,
      expanded,
    } = props;

    const { isCreatingNewDrawing } = useIsCreatingNewDrawing();
    const selectedDrawingTool = useSelectedDrawingTool();
    const [isHoverDragEnabled, setIsHoverDragEnabled] = React.useState(false);

    const handleOnDragMove = (e) => {
      const updatedDetails = moveActionPreProcessor(props, e.target.x(), e.target.y());
      updateDrawing(id, drawingType, updatedDetails);
    };

    const handleOnDragEnd = (e) => {
      const updatedDetails = moveActionPreProcessor(props, e.target.x(), e.target.y());
      saveAndUpdateDrawing(id, drawingType, updatedDetails);
    };

    const onTransformShape = (e) => {
      const node = drawingRef.current;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      node.scaleX(1);
      node.scaleY(1);
      const width = Math.max(minDrawingWidth, node.width() * scaleX);
      const height = Math.max(minDrawingHeight, node.height() * scaleY);

      const updatedDetails = transformActionPreProcessor(props, width, height);
      updatedDrawingSizeDetails = updatedDetails;
      updateDrawing(id, drawingType, updatedDetails);
    };

    const onTransformEnd = (e) => {
      detachTransformer();
      saveAndUpdateDrawing(id, drawingType, updatedDrawingSizeDetails);
    };

    const handleOnClick = (e) => {
      if (selectedDrawingTool === OverlayTools.POINTER || isHoverDragEnabled) {
        e.cancelBubble = true;
        selectDrawing(id, drawingType);
        if (trRef.current && !isLocked) {
          trRef.current.nodes([drawingRef.current]);
          trRef.current.getLayer().batchDraw();
        }
      }
    };

    const detachTransformer = () => {
      trRef.current.detach();
      trRef.current.getLayer().batchDraw();
    };
    /**
     * If drawing gets unselected, remove transformer
     */
    if ((!isSelected || isLocked || expanded === false) && trRef?.current) {
      detachTransformer();
    }

    const handleMouseEnter = (e) => {
      if (
        !isCreatingNewDrawing &&
        (selectedDrawingTool === OverlayTools.POINTER ||
          DrawingTypeToOverlayToolMapping[drawingType] === selectedDrawingTool)
      ) {
        setIsHoverDragEnabled(true);
        e.target.getStage().container().className = 'cursor-pointer';
      }
    };

    const handleMouseLeave = (e) => {
      setIsHoverDragEnabled(false);

      if (!isCreatingNewDrawing) {
        e.target.getStage().container().className =
          DrawingToolsPointerClassNameMapping[selectedDrawingTool] || 'cursor-default';
      }
    };

    const handleMouseUp = (e) => {
      e.target.getStage().container().className =
        DrawingToolsPointerClassNameMapping[selectedDrawingTool] || 'cursor-default';
    };

    return (
      <>
        <Group
          id="withdrawingcontainer-component-group"
          onMouseDown={handleOnClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          x={x}
          y={y}
          draggable={
            !isLocked &&
            (selectedDrawingTool === OverlayTools.POINTER || isHoverDragEnabled)
          }
          onDragMove={handleOnDragMove}
          onDragEnd={handleOnDragEnd}
        >
          <Image
            id="withdrawingcontainer-component-image"
            x={width - 15}
            y={height - 15}
            image={isLocked ? lockedIcon : null}
            width={10}
            height={10}
          />
          <WrappedComponent
            id={`withdrawingcontainer-component-wrapped`}
            rectRef={drawingRef}
            {...props}
          />
        </Group>
        {resizableShapes.includes(drawingType) && (
          <Transformer
            id="withdrawingcontainer-component-transformer"
            ref={trRef}
            onMouseDown={(e) => {
              e.cancelBubble = true;
            }}
            boundBoxFunc={(oldBox, newBox) => {
              if (newBox.width < minDrawingWidth || newBox.height < minDrawingHeight) {
                if (newBox.width < minDrawingWidth) {
                  newBox.width = minDrawingWidth;
                }
                if (newBox.height < minDrawingHeight) {
                  newBox.height = minDrawingHeight;
                }
                trRef.current.stopTransform();
                return oldBox;
              }
              return newBox;
            }}
            rotateEnabled={false}
            flipEnabled={false}
            onTransform={onTransformShape}
            onTransformEnd={onTransformEnd}
            enabledAnchors={['middle-right', 'bottom-center', 'bottom-right']}
          />
        )}
      </>
    );
  };
  return ContainerComponent;
};

export default withDrawingContainer;
